import { useEffect, useState } from "react";
import { getSolutiontToProductId } from "../../service/ApiServicioTecnico";
import styled from "styled-components";
import AccordionsGroup from "./AccordionsGroup";
import Spinner from "../spinner/Spinner";
import Modal from "../modal/Modal";
import ShareButton from "../share/ShareButton";
import { useCatalogRoute } from "../../contexts/CatalogRouteContext";

/**
 * Un nodo del árbol de soluciones.
 *
 * Abierto o cerrado lo decide la URL, no un estado local: si el id está en
 * openIds (la cadena de ancestros de la solución de la URL) se abre y trae su
 * detalle. Al abrirse muestra sus documentos y, recursivamente, sus
 * subsoluciones.
 */
export default function Accordion({ solution, parent, openIds }) {
    const { openSolution, closeSolution } = useCatalogRoute();

    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);

    const solutionId = solution.solution_id ?? solution.id;
    const productId = solution.type_id;
    const name = solution.item_solution ? solution.item_solution.name : 'Consultar por estado de solución';
    const isOpen = openIds.has(solutionId);

    useEffect(() => {
        if (!isOpen) return;

        let cancelled = false;
        setLoading(true);

        getSolutiontToProductId(productId, solutionId)
            .then(response => {
                if (cancelled) return;
                setDetail(response?.data ?? response);
            })
            .catch(error => {
                if (cancelled) return;
                console.error('Error obteniendo la solución:', error);
                setDetail(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [isOpen, productId, solutionId]);

    const handleClick = () => {
        if (isOpen) {
            // Cerrar es volver al padre. Si es de primer nivel, se va el segmento.
            closeSolution(parent ? { id: parent.id, name: parent.name } : null);
        } else {
            openSolution({ id: solutionId, name });
        }
    };

    const documentos = (detail?.documentos ?? []).filter(doc => doc.state === 'approved');
    const subsoluciones = detail?.subsoluciones ?? [];

    return (
        <StyledAccordion>
            <div className="accordion-item">
                <h3 className="accordion-header">
                    <button className={`accordion-button ${isOpen ? '' : 'collapsed'}`}
                            type="button"
                            onClick={handleClick}
                            aria-expanded={isOpen}
                            style={{fontWeight: '700', fontSize: '1.2rem'}}>
                            {name}
                    </button>
                </h3>
                <div className={`accordion-collapse collapse ${isOpen ? 'show' : ''}`}>
                    <div className="accordion-body">
                        {isOpen && (
                            loading ? (
                                <>
                                    <Spinner/>
                                    <p>Cargando soluciones...</p>
                                </>
                            ) : detail ? (
                                <>
                                    <div className="accordionActions">
                                        <ShareButton title={name} label="Compartir esta solución" />
                                    </div>

                                    {documentos.length > 0 && (
                                        <div>
                                            {documentos.map(documento => (
                                                <div key={documento.id} style={{margin: '0.8rem 0rem'}}>
                                                    <Modal doc={documento} solution={{ id: solutionId, name }} />
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {subsoluciones.length > 0 && (
                                        <AccordionsGroup
                                            solutions={subsoluciones}
                                            parent={{ id: solutionId, name }}
                                            openIds={openIds}
                                        />
                                    )}

                                    {documentos.length === 0 && subsoluciones.length === 0 && (
                                        <p>No hay subsoluciones ni documentos disponibles</p>
                                    )}
                                </>
                            ) : (
                                <p>No hay soluciones disponibles</p>
                            )
                        )}
                    </div>
                </div>
            </div>
        </StyledAccordion>
    );
}

const StyledAccordion = styled.div`
    .accordion-header {
        display: flex;
        align-items: center;
  }

  .accordion-button {
        padding: 1rem;
        margin: 0;
        flex-grow: 1;
  }

  .accordion-item {
        width: 100%;
  }

  .accordionActions {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 0.5rem;
  }
`
